import { summarizeAgentEvent } from "./chat/agent-event-summary.ts";
import { clearLiveState, recordLiveState } from "./chat/chat-live-state.ts";
import { resolveToolStatusPhrase } from "./chat/chat-run-status-phrases.ts";
import { isSubagentRunActive } from "./chat/subagent-run-status.ts";
import { patchSubagentCardEverywhere } from "./chat/subagents-by-session.ts";
import { truncateText } from "./format.ts";
import type { ChatSubagentCard } from "./ui-types.ts";

const TOOL_STREAM_LIMIT = 50;
const TOOL_STREAM_THROTTLE_MS = 80;
const TOOL_OUTPUT_CHAR_LIMIT = 120_000;

export type AgentEventPayload = {
  runId: string;
  seq: number;
  stream: string;
  ts: number;
  sessionKey?: string;
  data: Record<string, unknown>;
};

export type ToolStreamEntry = {
  toolCallId: string;
  runId: string;
  sessionKey?: string;
  name: string;
  args?: unknown;
  output?: string;
  startedAt: number;
  updatedAt: number;
  message: Record<string, unknown>;
};

export type ToolStreamHost = {
  sessionKey: string;
  chatRunId: string | null;
  chatStream?: string | null;
  chatReasoningStream?: string | null;
  chatStreamStartedAt?: number | null;
  chatLiveRunHint?: string | null;
  lastError?: string | null;
  chatMainRun?: ChatSubagentCard | null;
  chatInjectedAt?: number | null;
  chatCollectQueueDepth?: number;
  compactionStatus?: CompactionStatus | null;
  toolStreamById: Map<string, ToolStreamEntry>;
  toolStreamOrder: string[];
  chatToolMessages: Record<string, unknown>[];
  toolStreamSyncTimer: number | null;
  chatSubagents?: ChatSubagentCard[];
  /**
   * Per-session sticky fallback for `chatLiveRunHint`. JAISIU-3153.
   * Mutated by `applyStickyLiveRunHint` below.
   */
  stickyLiveRunHintBySession?: Record<string, string | null>;
  chatActiveFileToolPath?: string | null;
  /**
   * When the main agent calls `sessions_spawn`, refresh the subagent registry
   * so orchestration transcript cards appear without waiting for the 30s poll
   * or a full page reload.
   */
  refreshSubagentRegistry?: () => void | Promise<void>;
};

/**
 * Mirror the host's live-run fields into the per-session cache so a
 * later tab-switch can restore the "Working…" indicator. The host
 * already nulls the cache through `clearChatWaitState` on terminal
 * transitions; this helper just keeps the cache fresh while the run
 * is in flight.
 *
 * JAISIU-activity-always-visible: called from every tool-stream /
 * lifecycle mutation that touches one of the live fields. Without
 * this the snapshot freezes at the chat.send moment and never
 * reflects "the agent is now editing `src/foo.ts`" or "subagent X
 * is currently running".
 */
function snapshotLiveStateFromHost(host: ToolStreamHost): void {
  const runId = host.chatRunId;
  const startedAt = host.chatStreamStartedAt;
  if (!runId || startedAt == null) {
    return;
  }
  recordLiveState(host, host.sessionKey, {
    runId,
    startedAt,
    stream: host.chatStream ?? "",
    reasoningStream: host.chatReasoningStream ?? "",
    liveRunHint: host.chatLiveRunHint ?? null,
    activeFileToolPath: host.chatActiveFileToolPath ?? null,
    mainRun: host.chatMainRun ?? null,
    compaction: host.compactionStatus ?? null,
    injectedAt: host.chatInjectedAt ?? null,
    collectQueueDepth: host.chatCollectQueueDepth ?? 0,
  });
}

/** Tool events that mint child sessions and should invalidate the registry UI. */
/**
 * JAISIU-activity-always-visible — `handleAgentEvent` refreshes the
 * subagent registry on every `lifecycle` and `tool` event for the
 * active session (added at the top of the handler). This helper
 * remains for the `sessions_spawn` boundary only — the per-event
 * refresh above covers the long tail of mid-run events that the
 * previous `sessions_spawn start|result`-only gate missed.
 */
export function shouldRefreshSubagentsOnToolEvent(name: string, phase: string): boolean {
  if (name !== "sessions_spawn") {
    return false;
  }
  return phase === "start" || phase === "result";
}

const FS_TOOL_NAMES = new Set(["write", "edit", "apply_patch"]);

function extractFsToolPath(toolName: string, args: unknown): string | null {
  if (!args || typeof args !== "object") {
    return null;
  }
  const p = args as Record<string, unknown>;
  if (toolName === "write" || toolName === "edit") {
    const pathVal = p.path ?? p.file_path ?? p.filePath;
    return typeof pathVal === "string" ? pathVal.trim() : null;
  }
  if (toolName === "apply_patch") {
    const patch = p.patch ?? p.input;
    if (typeof patch !== "string") {
      return null;
    }
    const match = patch.match(/^\*\*\*\s+(?:Update|Add|Delete)\s+File:\s+(.+)$/m);
    return match?.[1]?.trim() ?? null;
  }
  return null;
}

/**
 * Sticky memo for `chatLiveRunHint`. Mirrors `buildLiveRunStepsWithSticky`'s
 * contract: first non-empty hint latches per session; only a *different*
 * non-empty hint replaces it; the `null → null` cycle is dropped.
 * JAISIU-3153.
 */
function applyStickyLiveRunHint(host: ToolStreamHost, next: string | null): void {
  if (!host.stickyLiveRunHintBySession) {
    return;
  }
  const sessionKey = host.sessionKey;
  const trimmedNext = next?.trim() ?? "";
  const currentSticky = host.stickyLiveRunHintBySession[sessionKey] ?? null;
  const trimmedSticky = currentSticky?.trim() ?? "";
  if (trimmedNext) {
    host.stickyLiveRunHintBySession[sessionKey] =
      trimmedNext === trimmedSticky ? currentSticky : next;
  } else if (trimmedSticky) {
    // Keep the existing sticky; do nothing.
  } else {
    // null → null: drop. Avoids redundant re-renders.
    if (currentSticky !== null) {
      host.stickyLiveRunHintBySession[sessionKey] = null;
    }
  }
}

/**
 * JAISIU-composer-pill-stuck — clear the per-session sticky label so the
 * next session starts with no latched hint. Call this on terminal
 * chat events (`chat.final`, abort, hard reset) so a stale "Subagent · …"
 * or "Reading…" cannot survive into the next render after the run is
 * closed. Without this, the sticky latch contract intentionally keeps
 * the hint alive across transient nulls, which is correct for in-run
 * flapping but wrong at run boundaries.
 *
 * Idempotent: a no-op when the session has no sticky entry or the entry
 * is already null.
 */
export function clearStickyLiveRunHint(host: ToolStreamHost, sessionKey?: string): void {
  if (!host.stickyLiveRunHintBySession) {
    return;
  }
  const key = (sessionKey ?? host.sessionKey ?? "").trim();
  if (!key) {
    return;
  }
  if (host.stickyLiveRunHintBySession[key] !== null) {
    host.stickyLiveRunHintBySession[key] = null;
  }
}

/**
 * JAISIU-composer-pill-stuck — flip every still-running subagent card on
 * the active list to `done` so the composer footer pill
 * (`Subagent · <id>:<title>`) drops the moment the parent run closes.
 *
 * The subagent lifecycle stream normally flips cards to `done`/`error`
 * on the agent's own `lifecycle.end` / `lifecycle.error` event. That
 * misses two real-world failure modes:
 *
 *   1. The parent chat run aborts (Stop button, hard reset) before
 *      the child has emitted its terminal lifecycle — the orphan card
 *      stays at `running` and the pill sticks.
 *   2. The gateway drops the child lifecycle events (reconnect, WS
 *      flap) but the child session is actually gone server-side. Same
 *      symptom: stuck pill.
 *
 * Flipping every `running | spawning | queued | accepted | cold-start`
 * card to `done` on the parent run's terminal event is safe because the
 * composer pill's filter (`buildLiveRunSteps` in chat-live-run.ts)
 * already excludes all non-running statuses, so the visible state is
 * guaranteed to collapse.
 *
 * No-op when the host carries no subagent cards for the session.
 */
export function terminateOrphanSubagentsOnChatClose(
  host: ToolStreamHost,
  sessionKey?: string,
): void {
  const targetSessionKey = (sessionKey ?? host.sessionKey ?? "").trim();
  if (!targetSessionKey || !Array.isArray(host.chatSubagents)) {
    return;
  }
  let changed = false;
  const next = host.chatSubagents.map((card) => {
    // `chatSubagents` is scoped to the active session tab; `childSessionKey`
    // identifies the worker session, not the requester (targetSessionKey).
    if (!isSubagentRunActive(card.status)) {
      return card;
    }
    changed = true;
    return { ...card, status: "done" as const, updatedAt: Date.now() };
  });
  if (changed) {
    host.chatSubagents = next;
  }
}

function extractToolOutputText(value: unknown): string | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  const record = value as Record<string, unknown>;
  if (typeof record.text === "string") {
    return record.text;
  }
  const content = record.content;
  if (!Array.isArray(content)) {
    return null;
  }
  const parts = content
    .map((item) => {
      if (!item || typeof item !== "object") {
        return null;
      }
      const entry = item as Record<string, unknown>;
      if (entry.type === "text" && typeof entry.text === "string") {
        return entry.text;
      }
      return null;
    })
    .filter((part): part is string => Boolean(part));
  if (parts.length === 0) {
    return null;
  }
  return parts.join("\n");
}

function formatToolOutput(value: unknown): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  const contentText = extractToolOutputText(value);
  let text: string;
  if (typeof value === "string") {
    text = value;
  } else if (contentText) {
    text = contentText;
  } else {
    try {
      text = JSON.stringify(value, null, 2);
    } catch {
      // oxlint-disable typescript/no-base-to-string
      text = String(value);
    }
  }
  const truncated = truncateText(text, TOOL_OUTPUT_CHAR_LIMIT);
  if (!truncated.truncated) {
    return truncated.text;
  }
  return `${truncated.text}\n\n… truncated (${truncated.total} chars, showing first ${truncated.text.length}).`;
}

function buildToolStreamMessage(entry: ToolStreamEntry): Record<string, unknown> {
  const content: Array<Record<string, unknown>> = [];
  content.push({
    type: "toolcall",
    name: entry.name,
    arguments: entry.args ?? {},
  });
  if (entry.output) {
    content.push({
      type: "toolresult",
      name: entry.name,
      text: entry.output,
    });
  }
  return {
    role: "assistant",
    toolCallId: entry.toolCallId,
    toolName: entry.name,
    runId: entry.runId,
    content,
    timestamp: entry.startedAt,
  };
}

function trimToolStream(host: ToolStreamHost) {
  if (host.toolStreamOrder.length <= TOOL_STREAM_LIMIT) {
    return;
  }
  const overflow = host.toolStreamOrder.length - TOOL_STREAM_LIMIT;
  const removed = host.toolStreamOrder.splice(0, overflow);
  for (const id of removed) {
    host.toolStreamById.delete(id);
  }
}

function syncToolStreamMessages(host: ToolStreamHost) {
  host.chatToolMessages = host.toolStreamOrder
    .map((id) => host.toolStreamById.get(id)?.message)
    .filter((msg): msg is Record<string, unknown> => Boolean(msg));
}

export function flushToolStreamSync(host: ToolStreamHost) {
  if (host.toolStreamSyncTimer != null) {
    clearTimeout(host.toolStreamSyncTimer);
    host.toolStreamSyncTimer = null;
  }
  syncToolStreamMessages(host);
}

export function scheduleToolStreamSync(host: ToolStreamHost, force = false) {
  if (force) {
    flushToolStreamSync(host);
    return;
  }
  if (host.toolStreamSyncTimer != null) {
    return;
  }
  host.toolStreamSyncTimer = window.setTimeout(
    () => flushToolStreamSync(host),
    TOOL_STREAM_THROTTLE_MS,
  );
}

export function resetToolStream(host: ToolStreamHost) {
  host.toolStreamById.clear();
  host.toolStreamOrder = [];
  host.chatToolMessages = [];
  if (host.chatActiveFileToolPath !== undefined) {
    host.chatActiveFileToolPath = null;
  }
  flushToolStreamSync(host);
}

export type CompactionStatus = {
  active: boolean;
  startedAt: number | null;
  completedAt: number | null;
};

type CompactionHost = ToolStreamHost & {
  compactionStatus?: CompactionStatus | null;
  compactionClearTimer?: number | null;
};

const COMPACTION_TOAST_DURATION_MS = 5000;

export function handleCompactionEvent(host: CompactionHost, payload: AgentEventPayload) {
  const data = payload.data ?? {};
  const phase = typeof data.phase === "string" ? data.phase : "";

  // Clear any existing timer
  if (host.compactionClearTimer != null) {
    window.clearTimeout(host.compactionClearTimer);
    host.compactionClearTimer = null;
  }

  if (phase === "start") {
    host.compactionStatus = {
      active: true,
      startedAt: Date.now(),
      completedAt: null,
    };
  } else if (phase === "end") {
    host.compactionStatus = {
      active: false,
      startedAt: host.compactionStatus?.startedAt ?? null,
      completedAt: Date.now(),
    };
    // Auto-clear the toast after duration
    host.compactionClearTimer = window.setTimeout(() => {
      host.compactionStatus = null;
      host.compactionClearTimer = null;
    }, COMPACTION_TOAST_DURATION_MS);
  }
}

export function handleAgentEvent(host: ToolStreamHost, payload?: AgentEventPayload) {
  if (!payload) {
    return;
  }

  // JAISIU-activity-always-visible — refresh the registry on every
  // `lifecycle` and `tool` event for the active session. The previous
  // gate (`stream === "tool"` + `sessions_spawn start|result`) only
  // fired at the spawn / tool boundaries — every step between
  // those (think, text-delta, mid-run lifecycle) hit the wire but
  // never refreshed the local subagent cards. With the 30-second
  // poll only firing on the Fleet Console tab, mid-run progress
  // was invisible until completion. The cost is one
  // `subagent.registry` RPC per event — cheap, and the operator
  // finally sees the activity bubble move.
  if (payload.stream === "operator-alert") {
    const kind = typeof payload.data?.kind === "string" ? payload.data.kind : "";
    // synthesis-incomplete is a soft harness finding — the chat bubble may
    // already contain a valid summary; do not paint the global error banner.
    if (kind === "synthesis-incomplete") {
      return;
    }
    const userMessage =
      typeof payload.data?.userMessage === "string" ? payload.data.userMessage.trim() : "";
    const summary = typeof payload.data?.summary === "string" ? payload.data.summary.trim() : "";
    const displayLine = userMessage || summary;
    const sessionOk = !payload.sessionKey || payload.sessionKey.trim() === host.sessionKey.trim();
    // The old expression was `payload.runId === host.chatRunId || host.chatRunId == null`,
    // which is TRUE for every run whenever the operator is idle. A provider
    // error raised by a background session therefore painted its text as
    // `lastError` in whichever chat the operator happened to be looking at.
    // Session ownership is now a hard requirement; run ownership only
    // disambiguates between runs of the SAME session.
    const runOk = !payload.runId || payload.runId === host.chatRunId || host.chatRunId == null;
    if (displayLine && sessionOk && runOk) {
      host.lastError = displayLine;
    }
    return;
  }

  if (payload.stream === "lifecycle" || payload.stream === "tool") {
    void host.refreshSubagentRegistry?.();
  }

  // Handle compaction events. Gated on session ownership for the same reason
  // as operator alerts above: "Compacting context…" is a per-session status
  // strip, so a background session compacting must not paint it over the chat
  // the operator is reading.
  if (payload.stream === "compaction") {
    const compactionSessionOk =
      !payload.sessionKey || payload.sessionKey.trim() === host.sessionKey.trim();
    if (compactionSessionOk) {
      handleCompactionEvent(host as CompactionHost, payload);
    }
    return;
  }

  if (payload.stream === "reasoning" && payload.runId === host.chatRunId) {
    const text = typeof payload.data?.text === "string" ? payload.data.text : "";
    if (text.trim() && host.chatReasoningStream !== undefined) {
      host.chatReasoningStream = text;
    }
    return;
  }

  if (payload.stream === "lifecycle") {
    const data = payload.data ?? {};
    const phase = typeof data.phase === "string" ? data.phase : "";
    const runId = payload.runId;
    const err = typeof data.error === "string" ? data.error : undefined;

    patchSubagentCardEverywhere(
      host as object,
      { runId },
      (c) => {
        if (c.runId !== runId) {
          return c;
        }
        const events = summarizeAgentEvent(
          c.events ?? [],
          runId,
          payload.seq,
          payload.ts,
          "lifecycle",
          data,
        );
        const updated = { ...c, events };
        if (phase === "end" || phase === "error") {
          if (phase === "error") {
            return {
              ...updated,
              status: "error" as const,
              error: err ?? c.error ?? "subagent error",
              updatedAt: payload.ts,
            };
          }
          return { ...updated, status: "done" as const, updatedAt: payload.ts };
        }
        if (phase === "initializing") {
          return { ...updated, status: "cold-start" as const, phase, updatedAt: payload.ts };
        }
        return { ...updated, phase, updatedAt: payload.ts };
      },
      host.chatSubagents,
      (cards) => {
        host.chatSubagents = cards;
      },
    );
    if (payload.runId === host.chatRunId) {
      if (host.chatLiveRunHint !== undefined) {
        let newHint: string | null = host.chatLiveRunHint;
        if (phase === "thinking") {
          newHint = "Thinking…";
        } else if (phase === "tool") {
          const toolName = typeof data.name === "string" ? data.name : "tool";
          // JAISIU-processing-transparency — surface a contextually
          // specific phrase ("Reading PDF…", "Diffing merge…",
          // "Calling tool X…") instead of the generic
          // "Running <tool>…" pill. Phrase builder is pure; args
          // are passed through so it can pull the file path or
          // command out for the leaf.
          newHint = resolveToolStatusPhrase(toolName, data.arguments ?? data.args);
        } else if (phase === "end" || phase === "error") {
          newHint = null;
          if (host.chatActiveFileToolPath !== undefined) {
            host.chatActiveFileToolPath = null;
          }
        }
        if (newHint !== host.chatLiveRunHint) {
          host.chatLiveRunHint = newHint;
        }
        applyStickyLiveRunHint(host, newHint);
      }
      if (host.chatMainRun) {
        const events = summarizeAgentEvent(
          host.chatMainRun.events ?? [],
          runId,
          payload.seq,
          payload.ts,
          "lifecycle",
          data,
        );
        host.chatMainRun = {
          ...host.chatMainRun,
          events,
          phase,
          updatedAt: payload.ts,
          status: phase === "error" ? "error" : phase === "end" ? "done" : host.chatMainRun.status,
          error: phase === "error" ? (err ?? host.chatMainRun.error) : host.chatMainRun.error,
        };
      }
      // JAISIU-activity-always-visible: refresh the per-session snapshot
      // when a lifecycle event changes the hint / phase / file path so
      // a tab-switch mid-run lands on the freshest state.
      snapshotLiveStateFromHost(host);
    }
    return;
  }

  if (payload.stream !== "tool") {
    return;
  }
  const sessionKey = typeof payload.sessionKey === "string" ? payload.sessionKey : undefined;
  const data = payload.data ?? {};
  const name = typeof data.name === "string" ? data.name : "tool";
  const phase = typeof data.phase === "string" ? data.phase : "";
  // The per-event refresh of `subagent.registry` is fired at the
  // top of this handler for every `tool` event — no need to call it
  // again here. The `shouldRefreshSubagentsOnToolEvent` helper
  // remains for callers that branch on tool names; the broad
  // agent-event refresh that operators depend on lives at the top.
  void name;
  void phase;
  void shouldRefreshSubagentsOnToolEvent;
  const short = `${name} · ${phase}`;
  const hint = truncateText(short, 400).text;
  const toolPatched = patchSubagentCardEverywhere(
    host as object,
    { runId: payload.runId, childSessionKey: sessionKey },
    (c) => {
      const match = c.runId === payload.runId || (sessionKey && c.childSessionKey === sessionKey);
      if (!match) {
        return c;
      }
      const events = summarizeAgentEvent(
        c.events ?? [],
        payload.runId,
        payload.seq,
        payload.ts,
        "tool",
        data,
      );
      return {
        ...c,
        toolHint: hint,
        phase: phase || c.phase,
        updatedAt: payload.ts,
        events,
      };
    },
    host.chatSubagents,
    (cards) => {
      host.chatSubagents = cards;
    },
  );
  if (toolPatched) {
    return;
  }
  if (sessionKey && sessionKey !== host.sessionKey) {
    return;
  }
  // Fallback: only accept session-less events for the active run.
  if (!sessionKey && host.chatRunId && payload.runId !== host.chatRunId) {
    return;
  }
  if (host.chatRunId && payload.runId !== host.chatRunId) {
    return;
  }
  if (!host.chatRunId) {
    return;
  }

  const toolCallId = typeof data.toolCallId === "string" ? data.toolCallId : "";
  if (!toolCallId) {
    return;
  }
  if (phase === "start") {
    const args = data.args;
    if (host.chatLiveRunHint !== undefined) {
      // JAISIU-processing-transparency — resolve a specific phrase
      // for the in-flight tool. The activity pill no longer shows
      // the bare `<name> · <phase>` short form (e.g. "bash · start")
      // which operators found too cryptic; the phrase builder
      // turns it into "Running `ls -la`…" / "Reading file.txt…"
      // etc. and bounds the result to keep the pill from wrapping.
      const phaseName = typeof data.phase === "string" ? data.phase : "";
      if (phaseName === "start" || phaseName === "result") {
        host.chatLiveRunHint = resolveToolStatusPhrase(name, data.arguments ?? data.args);
        applyStickyLiveRunHint(host, host.chatLiveRunHint);
      } else {
        // `progress` / `partial` / unknown phase — leave the hint
        // latched on whatever the `start` event set. Showing a new
        // pill per partial chunk would flicker the activity strip.
      }
    }
    if (host.chatActiveFileToolPath !== undefined && FS_TOOL_NAMES.has(name)) {
      const path = extractFsToolPath(name, args);
      host.chatActiveFileToolPath = path || null;
    }
    if (host.chatMainRun && payload.runId === host.chatRunId) {
      const events = summarizeAgentEvent(
        host.chatMainRun.events ?? [],
        payload.runId,
        payload.seq,
        payload.ts,
        "tool",
        data,
      );
      host.chatMainRun = {
        ...host.chatMainRun,
        // JAISIU-processing-transparency — main-run pill gets the
        // friendly phrase; the subagent card above keeps the raw
        // `name · phase` form for the per-card debug surface.
        toolHint: resolveToolStatusPhrase(name, data.arguments ?? data.args),
        phase: phase || host.chatMainRun.phase,
        updatedAt: payload.ts,
        events,
      };
    }
    // JAISIU-activity-always-visible: a tool-start sets both the
    // hint and the active file path — refresh the snapshot so a
    // tab-switch mid-run lands on the in-progress edit.
    snapshotLiveStateFromHost(host);
  }
  const args = phase === "start" ? data.args : undefined;
  const output =
    phase === "update"
      ? formatToolOutput(data.partialResult)
      : phase === "result"
        ? formatToolOutput(data.result)
        : undefined;

  const now = Date.now();
  let entry = host.toolStreamById.get(toolCallId);
  if (!entry) {
    entry = {
      toolCallId,
      runId: payload.runId,
      sessionKey,
      name,
      args,
      output: output || undefined,
      startedAt: typeof payload.ts === "number" ? payload.ts : now,
      updatedAt: now,
      message: {},
    };
    host.toolStreamById.set(toolCallId, entry);
    host.toolStreamOrder.push(toolCallId);
  } else {
    entry.name = name;
    if (args !== undefined) {
      entry.args = args;
    }
    if (output !== undefined) {
      entry.output = output || undefined;
    }
    entry.updatedAt = now;
  }

  entry.message = buildToolStreamMessage(entry);
  trimToolStream(host);
  if (phase === "result" && host.chatActiveFileToolPath !== undefined && FS_TOOL_NAMES.has(name)) {
    host.chatActiveFileToolPath = null;
  }

  scheduleToolStreamSync(host, phase === "result");
}
